// Read-only body of a child workspace's transaction, rendered from the parent
// workspace context inside TawPreviewDrawer. Shows deal facts, the parent's
// commission share, and closing-path progress. Carries no page chrome — the
// header (workspace name, address, status chip, "Read-only view", "Open in
// workspace") lives in TawPreviewDrawer.js.

import React from 'react';
import { deriveFinancialFieldsForModule, deriveExpCappingStatus } from '../../../commissions/index';
import { isExpStint } from '../../../commissions/brokerage';
import { planLeaderShareForChild } from '../../../commissions/modules/plan/teamSplit';
import { useBrokerage } from '../../../contexts/BrokerageContext';
import { formatCurrency, formatShortDate } from '../../../utils/formatting';
import { transactionTypeLabel } from '../../../config/statuses';
import { deriveClearedCount } from './v2/OverviewClosingPath';
import { formatClosingCountdown } from '../../../utils/portalStatus';

function InfoRow({ label, value }) {
    if (!value && value !== 0) return null;
    return (
        <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium uppercase tracking-wide text-[color:var(--text-faint)]">{label}</span>
            <span className="text-sm text-[color:var(--text-secondary)]">{value}</span>
        </div>
    );
}

function Section({ title, headerExtra, children }) {
    return (
        <section className="rounded-lg border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{title}</h3>
            {headerExtra}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
                {children}
            </div>
        </section>
    );
}

const fmt = (v) => formatCurrency(Math.abs(v));
const fmtSigned = (v) => {
    const abs = Math.abs(v);
    const s = `$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return v < 0 ? `(${s})` : s;
};
const fmtPct = (v) => `${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;

// Status-badge colour map, shared with TawPreviewDrawer's header chip so both
// surfaces classify a TAW transaction's status identically.
const statusColors = {
    Sold: 'bg-[color:var(--surface-success)] text-[color:var(--color-success)]',
    Active: 'bg-[color:var(--surface-info)] text-[color:var(--action-link-color)]',
    Contract: 'bg-[color:var(--surface-warning)] text-[color:var(--color-warning)]',
    Terminated: 'bg-[color:var(--surface-danger)] text-[color:var(--color-danger)]',
    Lead: 'bg-[color:var(--surface-subtle)] text-[color:var(--text-secondary)]',
    Referred: 'bg-[color:var(--surface-info)] text-[color:var(--color-primary)]',
};

export function tawStatusBadgeClass(status) {
    return statusColors[status] || 'bg-[color:var(--surface-subtle)] text-[color:var(--text-secondary)]';
}

// leaderPlanState and teamSplitRecords: a plan leader's current cap-year state
// and its team-split income records, from AppRouter's team-list pipeline, so
// this view and the list row show the same number. Unused on eXp.
export default function ParentChildTransactionView({ transaction, expModuleSettings, leaderPlanState = null, teamSplitRecords = [] }) {
    const settings = expModuleSettings ?? {};
    const splitPercentToAgent = Number(transaction._splitPercentToAgent ?? 100);
    const parentSharePercent = 100 - splitPercentToAgent;
    const childGross = Number(transaction.grossCommission) || 0;

    // Referral comes off first; split is applied to the post-referral
    // commission PLUS admin fee (the effective GCI the TW shares in —
    // mirrors workspaceService.upsertTwTeamSplitIncome).
    const referralDeducted = Number(transaction.referralFeeAmount) || 0;
    const commissionAfterReferral = Number(transaction.commissionAfterReferral ?? transaction.grossCommission) || 0;
    const adminFeeIncluded = Number(transaction.adminFee) || 0;
    const splitBase = commissionAfterReferral + adminFeeIncluded;
    const parentGross = splitBase * (parentSharePercent / 100);

    const teamSplitPct = Number(settings.teamSplitPercentage) || 100;

    // Apply parent's own pipeline to parent's share.
    // Pass splitBase as grossCommission so the calculator's teamSplitFraction can
    // correctly scale TRF/RMF/CSTF. combinedSplitPct encodes both the parent/child
    // split and the parent's own internal team split so all flat fees are scaled once.
    const combinedSplitPct = parentSharePercent * (teamSplitPct / 100);
    const { brokerage } = useBrokerage();
    const leaderPlan = isExpStint(brokerage?.current) ? null : brokerage?.current?.plan || null;
    const { derived, planBrokerPaid } = React.useMemo(() => {
        if (!splitBase || !parentSharePercent) return { derived: null, planBrokerPaid: 0 };
        const parentDeal = {
            ...transaction,
            grossCommission: splitBase,
            commissionInputMode: 'flat',
            referralFee: 0,
            adminFee: 0,
            expenses: [],
            // Ensure shouldZeroNet never fires for a valid flat commission.
            salePrice: Number(transaction.salePrice) || Number(transaction.listPrice) || Number(transaction.budget) || splitBase,
        };
        const parentSettings = { ...settings, teamSplitPercentage: combinedSplitPct };
        // An eXp parent prices exactly as before: its stored counters, not a
        // one-deal list that would read as zero.
        if (isExpStint(brokerage?.current)) {
            return { derived: deriveFinancialFieldsForModule(parentDeal, parentSettings), planBrokerPaid: 0 };
        }
        // A plan parent prices its share exactly as the team list row does
        // (AppRouter's enrich): with its current plan, at its current cap-year
        // state, and for a Sold deal from the stored team-split record when the
        // leader has it. The child deal's own pricingPlan stamp names a stint
        // in the CHILD workspace, so it plays no part.
        const share = planLeaderShareForChild(transaction, {
            plan: brokerage.current.plan,
            leaderTeamSplitPercentage: settings.teamSplitPercentage,
            periodState: leaderPlanState || undefined,
            teamSplitRecords,
        });
        // A Sold deal read from its team-split record was priced against the
        // state stored on that record, so its cap line reads from there too.
        const period = share.record ? share.record.pricedAgainstCache : leaderPlanState;
        return { derived: share.derived, planBrokerPaid: Number(period?.brokerPaid) || 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transaction, splitBase, combinedSplitPct, settings, brokerage, leaderPlanState, teamSplitRecords]);

    const parentNet = derived?.netIncome != null ? Number(derived.netIncome) : parentGross;

    // Live capping context is shown on the Income page; here we only need the
    // values referenced by the deductions/fees breakdown below.
    const cappingStatus = derived?.derivedCappingStatus || deriveExpCappingStatus(settings);
    const isCrossingCap = !!derived?.isCrossingCap;

    const companyDollarPaid  = Number(settings.companyDollarPaidCurrentPeriod) || 0;
    const companyDollarCap   = Number(settings.companyDollarCap) || 16000;
    const cstfTier1Amount    = Number(settings.cappedStatusFeeTier1) || 250;
    const cstfTier2Amount    = Number(settings.cappedStatusFeeTier2) || 75;
    const rmfPaid            = Number(settings.riskManagementFeePaidCurrentPeriod) || 0;
    const rmfAnnualCap       = Number(settings.riskManagementFeeAnnualCap) || 750;
    const companySplitPct    = Number(settings.companySplitPercentage) || 20;

    const totalDeductions = Math.max(0, parentGross - parentNet);
    const companySplitAmount = Number(derived?.companySplitAmount) || 0;
    const cappedStatusFee    = Number(derived?.cappedStatusFee) || 0;
    const trf                = Number(derived?.transactionReviewFeeCharged) || 0;
    const rmf                = Number(derived?.rmfApplied) || 0;
    const agentGrossShare    = Number(derived?.agentGrossShare) || parentGross;
    const royaltyAmount      = Number(derived?.royaltyAmount) || 0;
    const planFees           = Array.isArray(derived?.planFees) ? derived.planFees : [];

    const clientNames = Array.isArray(transaction.clients)
        ? transaction.clients.map(c => `${c.firstName || ''} ${c.lastName || ''}`.trim()).filter(Boolean).join(', ')
        : '';

    // D-21: closing-path progress — same idiom as PortalStatusBar.
    const { x: clearedCount, n: applicableCount } = deriveClearedCount(transaction || {}, transaction?.completedMilestones || {});
    const closingCountdown = formatClosingCountdown(transaction?.closeDate);
    const closingPct = applicableCount > 0 ? Math.round((clearedCount / applicableCount) * 100) : 0;

    // D-21 progress summary and the raw milestone dates below it both describe
    // the same underlying facts — listDate/contractDate/closeDate/occupancyDate
    // are TRANSACTION_MILESTONES entries, so the X-of-N count is a rollup of the
    // very dates the grid lists. They previously rendered as two separately
    // bordered cards ("Closing path" and "Timeline"), reading as duplicates of
    // each other. One "Timeline" card now leads with the at-a-glance progress
    // strip and follows with the individual dates as supporting detail.
    const closingProgress = (
        <>
            {(closingCountdown || applicableCount > 0) && (
                <div className="mb-2 flex flex-col gap-2 border-b border-[color:var(--border-color)] pb-2 sm:flex-row sm:items-center sm:justify-between">
                    {closingCountdown && (
                        <span className="inline-flex items-center gap-2 self-start rounded-full bg-[color:var(--surface-info)] px-3 py-1 text-sm font-semibold text-[color:var(--color-accent)]">
                            {closingCountdown.tone !== 'past' && <span aria-hidden="true">🎉</span>}
                            {closingCountdown.label}
                        </span>
                    )}
                    {applicableCount > 0 && (
                        <div className="w-full sm:max-w-xs">
                            <div className="mb-1 flex items-center justify-between text-xs font-medium text-[color:var(--text-muted)]">
                                <span>Progress</span>
                                <span>{clearedCount} of {applicableCount} steps complete</span>
                            </div>
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--surface-subtle)]">
                                <div
                                    className="h-full rounded-full bg-[color:var(--color-accent)] transition-all duration-700 ease-out"
                                    style={{ width: `${closingPct}%` }}
                                    role="progressbar"
                                    aria-valuenow={closingPct}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );

    return (
        <div className="space-y-3">
            {/* Timeline — progress-at-a-glance, then the underlying dates */}
            <Section title="Timeline" headerExtra={closingProgress}>
                <InfoRow label="List date" value={formatShortDate(transaction.listDate)} />
                <InfoRow label="Contract date" value={formatShortDate(transaction.contractDate)} />
                <InfoRow label="Close date" value={formatShortDate(transaction.closeDate)} />
                <InfoRow label="Occupancy date" value={formatShortDate(transaction.occupancyDate)} />
            </Section>

            {/* Property */}
            <Section title="Property">
                <InfoRow label="Address" value={transaction.propertyStreet} />
                <InfoRow label="City / State" value={transaction.propertyCityStateZip} />
                <InfoRow label="Type" value={transactionTypeLabel(transaction.transactionType)} />
                <InfoRow label="Client(s)" value={clientNames} />
            </Section>

            {/* Contract */}
            {(transaction.salePrice || transaction.earnestMoneyAmount) && (
                <Section title="Contract">
                    <InfoRow label="Sale price" value={transaction.salePrice ? formatCurrency(transaction.salePrice) : null} />
                    <InfoRow label="List price" value={transaction.listPrice ? formatCurrency(transaction.listPrice) : null} />
                    <InfoRow label="Earnest money" value={transaction.earnestMoneyAmount ? formatCurrency(transaction.earnestMoneyAmount) : null} />
                    <InfoRow label="Seller concessions" value={transaction.sellerConcessions ? formatCurrency(transaction.sellerConcessions) : null} />
                </Section>
            )}

            {/* ── Your Commission ──────────────────────────────────── */}
            <section className="rounded-lg border border-[color:var(--color-accent)] bg-[color:var(--surface-info)] p-4 space-y-3">
                    <div>
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-[color:var(--action-link-color)]">Your Commission</h3>
                        <p className="mt-0.5 text-xs text-[color:var(--action-link-color)]">
                            Your {parentSharePercent}% share of this {transaction._workspaceName || 'child'} transaction, run through your own pipeline
                        </p>
                    </div>

                    {/* Split calculation */}
                    <div className="rounded-md bg-[color:var(--surface-primary)] border border-[color:var(--color-accent)] px-4 divide-y divide-[color:var(--border-color)]">
                        <div className="flex items-center justify-between py-2">
                            <span className="text-sm text-[color:var(--text-secondary)]">Child gross commission</span>
                            <span className="text-sm font-semibold text-[color:var(--text-secondary)]">{fmt(childGross)}</span>
                        </div>
                        {referralDeducted > 0 && (
                            <div className="flex items-center justify-between py-2 pl-4">
                                <span className="text-sm text-[color:var(--text-muted)]">Referral fee (off the top)</span>
                                <span className="text-sm font-semibold text-[color:var(--text-secondary)]">− {fmt(referralDeducted)}</span>
                            </div>
                        )}
                        {referralDeducted > 0 && (
                            <div className="flex items-center justify-between py-2 pl-4">
                                <span className="text-sm text-[color:var(--text-muted)]">Commission after referral</span>
                                <span className="text-sm font-semibold text-[color:var(--text-secondary)]">{fmt(commissionAfterReferral)}</span>
                            </div>
                        )}
                        {adminFeeIncluded > 0 && (
                            <div className="flex items-center justify-between py-2 pl-4">
                                <span className="text-sm text-[color:var(--text-muted)]">Admin fee (added in)</span>
                                <span className="text-sm font-semibold text-[color:var(--text-secondary)]">+ {fmt(adminFeeIncluded)}</span>
                            </div>
                        )}
                        {adminFeeIncluded > 0 && (
                            <div className="flex items-center justify-between py-2 pl-4">
                                <span className="text-sm text-[color:var(--text-muted)]">Split base</span>
                                <span className="text-sm font-semibold text-[color:var(--text-secondary)]">{fmt(splitBase)}</span>
                            </div>
                        )}
                        <div className="flex items-center justify-between py-2 pl-4">
                            <span className="text-sm text-[color:var(--text-muted)]">
                                Your share ({parentSharePercent}%{(referralDeducted > 0 || adminFeeIncluded > 0) ? ' of split base' : ''})
                            </span>
                            <span className="text-sm font-semibold text-[color:var(--text-primary)]">{fmt(parentGross)}</span>
                        </div>
                    </div>

                    {/* Breakdown */}
                    {derived && (
                        <div className="space-y-3">
                            {/* Effective GCI */}
                            <div className="rounded-lg border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] px-4 py-3 shadow-sm">
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <p className="text-sm font-medium text-[color:var(--text-muted)] uppercase tracking-wide">Your Gross Share</p>
                                        <p className="text-xs text-[color:var(--text-muted)] mt-1">Your {parentSharePercent}% of the post-referral commission{adminFeeIncluded > 0 ? ' + admin fee' : ''}</p>
                                    </div>
                                    <p className="text-lg font-semibold text-[color:var(--text-primary)]">{fmt(parentGross)}</p>
                                </div>
                            </div>

                            {/* Deductions — collapsible */}
                            <details className="rounded-lg border border-[color:var(--color-warning)]/20 bg-[color:var(--surface-warning)] px-4 py-3 shadow-sm group">
                                <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
                                    <div>
                                        <p className="text-sm font-medium uppercase tracking-wide" style={{ color: 'var(--text-primary)' }}>Deductions / Fees</p>
                                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Expand to view individual fee calculations</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{fmtSigned(-totalDeductions)}</p>
                                        <p className="text-xs group-open:hidden" style={{ color: 'var(--text-muted)' }}>Show details</p>
                                        <p className="text-xs hidden group-open:block" style={{ color: 'var(--text-muted)' }}>Hide details</p>
                                    </div>
                                </summary>

                                <div className="mt-4 space-y-2 border-t border-[color:var(--color-warning)]/20 pt-3">
                                    {/* Agent's share when workspace team split < 100% */}
                                    {teamSplitPct < 100 && (
                                        <div className="flex items-center justify-between gap-4 text-sm border-b border-[color:var(--color-warning)]/20 pb-2 mb-2">
                                            <span style={{ color: 'var(--text-primary)' }}>
                                                Your Agent Share ({fmtPct(teamSplitPct)}){' '}
                                                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>After internal team split</span>
                                            </span>
                                            <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{fmt(agentGrossShare)}</span>
                                        </div>
                                    )}

                                    {leaderPlan ? (
                                        <>
                                            {/* A plan leader's own rows, as the Financials ledger shows a plan deal */}
                                            {royaltyAmount > 0 && (
                                                <div className="flex items-center justify-between gap-4 text-sm">
                                                    <span style={{ color: 'var(--text-primary)' }}>Royalty ({fmtPct(leaderPlan.royalty?.pct || 0)})</span>
                                                    <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{fmtSigned(-royaltyAmount)}</span>
                                                </div>
                                            )}
                                            {companySplitAmount > 0 && (
                                                <div className="flex items-start justify-between gap-4 text-sm">
                                                    <div>
                                                        <span style={{ color: 'var(--text-primary)' }}>
                                                            {cappingStatus === 'capped'
                                                                ? `Brokerage split (${fmtPct(leaderPlan.cap?.postCapBrokerPct || 0)} after cap)`
                                                                : `Brokerage split (${fmtPct(leaderPlan.split.brokerPct)})`}
                                                        </span>
                                                        {isCrossingCap ? (
                                                            <p className="text-xs text-[color:var(--color-warning)] mt-0.5">
                                                                ⚡ Cap reached mid-transaction. Only {fmt(Number(derived.companyDollarRemaining))} was subject to {fmtPct(leaderPlan.split.brokerPct)}.
                                                            </p>
                                                        ) : leaderPlan.cap && cappingStatus !== 'capped' ? (
                                                            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                                                                Cap: {fmt(planBrokerPaid)} / {fmt(leaderPlan.cap.amount)}
                                                            </p>
                                                        ) : null}
                                                    </div>
                                                    <span className="font-medium shrink-0" style={{ color: 'var(--text-primary)' }}>{fmtSigned(-companySplitAmount)}</span>
                                                </div>
                                            )}
                                            {planFees.map((fee, i) => (
                                                <div key={fee.id || i} className="flex items-center justify-between gap-4 text-sm">
                                                    <span style={{ color: 'var(--text-primary)' }}>{fee.label}</span>
                                                    <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{fmtSigned(-(Number(fee.amount) || 0))}</span>
                                                </div>
                                            ))}
                                        </>
                                    ) : (
                                        <>
                                        {/* Company split (not capped) */}
                                        {cappingStatus === 'not_capped' && companySplitAmount > 0 && (
                                            <div className="flex items-start justify-between gap-4 text-sm">
                                                <div>
                                                    <span style={{ color: 'var(--text-primary)' }}>Company Split ({fmtPct(companySplitPct)})</span>
                                                    {isCrossingCap ? (
                                                        <p className="text-xs text-[color:var(--color-warning)] mt-0.5">
                                                            ⚡ Cap reached mid-transaction. Only {fmt(Number(derived.companyDollarRemaining))} was subject to {fmtPct(companySplitPct)}.
                                                        </p>
                                                    ) : (
                                                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                                                            Company Dollar: {fmt(companyDollarPaid)} / {fmt(companyDollarCap)}
                                                        </p>
                                                    )}
                                                </div>
                                                <span className="font-medium shrink-0" style={{ color: 'var(--text-primary)' }}>{fmtSigned(-companySplitAmount)}</span>
                                            </div>
                                        )}

                                        {/* Capped status fee (tier 1 or 2) */}
                                        {cappedStatusFee > 0 && (
                                            <div className="flex items-start justify-between gap-4 text-sm">
                                                <div>
                                                    <span style={{ color: 'var(--text-primary)' }}>
                                                        Capped Status Fee{' '}
                                                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                                            {cappingStatus === 'capped_tier1'
                                                                ? `Tier 1 — up to ${fmt(cstfTier1Amount)} per transaction`
                                                                : `Tier 2 — ${fmt(cstfTier2Amount)} per transaction`}
                                                        </span>
                                                    </span>
                                                </div>
                                                <span className="font-medium shrink-0" style={{ color: 'var(--text-primary)' }}>{fmtSigned(-cappedStatusFee)}</span>
                                            </div>
                                        )}

                                        {/* Transaction Review Fee */}
                                        {trf > 0 && (
                                            <div className="flex items-center justify-between gap-4 text-sm">
                                                <span style={{ color: 'var(--text-primary)' }}>
                                                    Transaction Review Fee{' '}
                                                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>eXp flat fee per transaction</span>
                                                </span>
                                                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{fmtSigned(-trf)}</span>
                                            </div>
                                        )}

                                        {/* Risk Management Fee */}
                                        {rmf > 0 && (
                                            <div className="flex items-start justify-between gap-4 text-sm">
                                                <div>
                                                    <span style={{ color: 'var(--text-primary)' }}>Risk Management Fee</span>
                                                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                                                        Period total: {fmt(rmfPaid)} / {fmt(rmfAnnualCap)} (resets on anniversary)
                                                    </p>
                                                </div>
                                                <span className="font-medium shrink-0" style={{ color: 'var(--text-primary)' }}>{fmtSigned(-rmf)}</span>
                                            </div>
                                        )}
                                        </>
                                    )}

                                    {totalDeductions === 0 && (
                                        <p className="text-sm text-[color:var(--text-muted)] italic">No deductions for this transaction.</p>
                                    )}
                                </div>
                            </details>

                            {/* Net Income */}
                            <div className="rounded-lg border border-[color:var(--color-accent)] bg-[color:var(--surface-info)] px-4 py-3 shadow-sm">
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <p className="text-sm font-medium text-[color:var(--text-primary)] uppercase tracking-wide">Your Net Income</p>
                                        <p className="text-xs text-[color:var(--action-link-color)] mt-1">Your gross share minus all brokerage deductions and fees</p>
                                    </div>
                                    <p className="text-xl font-semibold text-[color:var(--action-link-color)]">{fmt(parentNet)}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {!derived && (
                        <p className="text-sm text-[color:var(--action-link-color)] italic">No commission data — this transaction has no gross commission entered.</p>
                    )}
                </section>
        </div>
    );
}
